<template>
    <div>
        <!-- Program details (static) -->
        <div :class="containerClasses">
            <!-- Symbol or Logo -->
            <div class="lg:flex gap-6">
                <editable-symbol-field
                    v-if="brandingType === 'symbol' && symbol"
                    :symbol-wrapper-classes="[
                        'relative h-20 w-20 lg:h-32 lg:w-32 flex-shrink-0 bg-white bg-cover bg-center',
                        layout === 'one' ? 'rounded' : '', corners
                    ]"
                    symbol-name="portal_layout_file_symbol"
                    v-model:symbol-path="portalContentAndDesignStore.srcRoutes.portal_layout_file_symbol"
                    :symbol-upload-url="portalContentAndDesignStore.getFileUploadRoute"
                    :symbol-delete-url="portalContentAndDesignStore.getFileDeleteRoute"
                />

                <div
                    v-else-if="brandingType === 'logo' && logo"
                    class="relative w-auto"
                    style="max-width: 256px"
                >
                    <editable-image-field
                        v-model:image-path="portalContentAndDesignStore.srcRoutes.portal_layout_file_logo"
                        img-name="portal_layout_file_logo"
                        title="Logo"
                        :image-upload-url="portalContentAndDesignStore.getFileUploadRoute"
                        :image-delete-url="portalContentAndDesignStore.getFileDeleteRoute"
                        img-class="w-auto h-auto"
                    ></editable-image-field>
                </div>

                <!-- Title & Description -->
                <div class="flex-1 space-y-4 mt-4 lg:mt-0">
                    <div :class="textColorClass">
                        <div class="flex items-center justify-between gap-6">
                            <editable-text-field
                                text-type="h2"
                                v-model="portalContentAndDesignStore.leadsPageSettings.program_leads_page_title"
                            ></editable-text-field>

                            <editable-btn
                                size="small"
                                class="hidden lg:flex shrink-0"
                                :type="buttonColor"
                                :corners="portalContentAndDesignStore.getPublicPageButtonCorners"
                                v-model="portalContentAndDesignStore.translationSettings.portal_translation_program_page_visit_website"
                                :href="landingPageUrl"
                                link-target="_blank"
                                icon-name="arrow-right-up"
                                icon-right
                            />
                        </div>
                        <editable-text-editor-field
                            v-if="portalContentAndDesignStore.leadsPageSettings.program_leads_page_description"
                            v-model="portalContentAndDesignStore.leadsPageSettings.program_leads_page_description"
                            :class="[
                                'partnero-page-content-section text-md space-y-2 text-opacity-80',
                                textColorClass
                            ]"
                        />

                        <editable-btn
                            size="default"
                            class="w-full mt-4 flex lg:hidden"
                            :type="buttonColor"
                            :corners="portalContentAndDesignStore.getPublicPageButtonCorners"
                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_program_page_visit_website"
                            :href="landingPageUrl"
                            link-target="_blank"
                            icon-name="arrow-right-up"
                            icon-right
                        />
                    </div>

                    <!-- Log in / Sign up links -->
                    <div v-if="portalContentAndDesignStore.leadsPageSettings.program_leads_include_signup_links" class="flex items-center gap-2">
                        <editable-btn
                            size="small"
                            :type="buttonColor"
                            :corners="portalContentAndDesignStore.getPublicPageButtonCorners"
                            :href="loginLink"
                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_program_page_log_in"
                        />
                        <editable-text-field
                            v-if="!disableRegistration"
                            class="text-sm"
                            :class="textColorClass"
                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_program_page_or"
                            text-type="span"
                        >
                        </editable-text-field>

                        <editable-btn
                            v-if="!disableRegistration"
                            size="small"
                            :type="buttonColor"
                            :corners="portalContentAndDesignStore.getPublicPageButtonCorners"
                            :href="signupLink"
                            :link-target="portalContentAndDesignStore.isExternalRegisterRoute ? '_blank' : '_self'"
                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_program_page_sign_up"
                        />
                    </div>
                </div>
            </div>

            <!-- Additional section -->
            <div
                v-if="portalContentAndDesignStore.leadsPageSettings.program_leads_include_additional_section
                  && portalContentAndDesignStore.leadsPageSettings.program_leads_page_additional_description"
                :class="[
                'bg-black bg-opacity-5 p-6',
                layout === 'one' ? 'rounded' : '', corners
            ]"
            >
                <editable-text-editor-field
                    :class="[
                    'partnero-page-content-section text-md text-opacity-80',
                    textColorClass
                ]"
                    v-model="portalContentAndDesignStore.leadsPageSettings.program_leads_page_additional_description"
                />
            </div>
        </div>
        <!-- Lead form -->
        <div :class="formWrapperClasses">

          <form-errors :store-form-errors="portalContentAndDesignStore.leadsFormErrors"/>
          <alert v-if="leadSubmissionSuccessMessage"
                 type="success"
          >
            {{ leadSubmissionSuccessMessage }}
          </alert>

            <form
                id="program-lead"
            >
                <input type="hidden" name="_token" :value="csrfToken" />

                <div class="mb-8">
                    <program-lead-logged-partner
                        :logged-partner="portalContentAndDesignStore.leadsPageSettings.logged_partner"
                        :login-config="portalContentAndDesignStore.leadsPageSettings.login_config"
                        :translations=" portalContentAndDesignStore.translationSettings"
                    />
                </div>

                <editable-text-field
                    text-type="custom"
                    :class="['text-lg md:text-2xl font-semibold mb-4', portalContentAndDesignStore.getPageContentTextColorClass]"
                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_leads_lead_details"
                />

                <leads-custom-fields />

                <div class="mt-8">
                    <editable-btn
                        :type="buttonColor"
                        :corners="portalContentAndDesignStore.getPublicPageButtonCorners"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_leads_submit_a_lead"
                        :disabled="submittingLeadForm"
                        :loading="submittingLeadForm"
                        @click="submitLead"
                    />
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import {computed, ref} from 'vue'

import {usePortalContentAndDesignStore} from "../../../../../stores/partners/portalContentAndDesignStore";
import EditableTextField from "../../../portal-editable-fields/EditableTextField.vue";
import EditableBtn from "../../../portal-editable-fields/EditableBtn.vue";
import EditableTextEditorField
    from "../../../portal-editable-fields/EditableTextEditorField.vue";
import EditableImageField from "../../../portal-editable-fields/EditableImageField.vue";
import EditableSymbolField from "../../../portal-editable-fields/EditableSymbolField.vue";
import LeadsCustomFields from "./includes/LeadsCustomFields.vue";
import Alert from "../../../../../elements/Alert.vue";
import FormErrors from "../Errors/FormErrors.vue";

const portalContentAndDesignStore = usePortalContentAndDesignStore()

const props = defineProps({
    csrfToken: {
        type: String,
        required: true
    }
})

const getFormActionRoute = route('partners_int.leads.submit')

const leadSubmissionSuccessMessage = ref(null)
const submittingLeadForm = ref(false)

const layout = computed(() => portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout)
const corners = computed(() => portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout_corners !== 'none'
    ? portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout_corners
    : '')

const textColorClass = computed(() => portalContentAndDesignStore.getPageContentTextColorClass)
const buttonColor = computed(() => portalContentAndDesignStore.getPageContentButtonColorClass)

const disableRegistration = computed(() => portalContentAndDesignStore.portalLayoutSettings.referral_portal_portal_disable_registrations_via_portal || false);

const symbol = computed(() => portalContentAndDesignStore.srcRoutes.portal_layout_file_symbol)
const logo = computed(() => portalContentAndDesignStore.srcRoutes.portal_layout_file_logo)
const brandingType = computed(() => portalContentAndDesignStore.leadsPageSettings.program_leads_page_branding_type)

const loginLink = computed(() => portalContentAndDesignStore.getLoginRoute)
const signupLink = computed(() => portalContentAndDesignStore.getRegisterRoute)
const landingPageUrl = computed(() => portalContentAndDesignStore.getLandingPageSettings.landing_page_url || '/')

const containerClasses = computed(() => {
    const classes = ['space-y-6 pb-8 lg:pb-12 pt-8 lg:pt-20'];
    if (layout.value !== 'one') classes.push('px-8 lg:px-14');
    return classes.join(' ');
})

const formWrapperClasses = computed(() => {
    const classes = ['pt-8 lg:pt-12 pb-8 lg:pb-20 space-y-12 border-t'];
    if (layout.value !== 'one') classes.push('px-8 lg:px-14');
    return [classes.join(' '), textColorClass.value];
})

const submitLead = () => {

    leadSubmissionSuccessMessage.value = null
    submittingLeadForm.value = true

    portalContentAndDesignStore.resetFormErrors('leads')

    axios.post(getFormActionRoute, portalContentAndDesignStore.leadsFormData)
        .then(response => {
            if (+response.data.status === 1) {

                leadSubmissionSuccessMessage.value = response.data.message

                portalContentAndDesignStore.resetLeadsFormAndErrorData()
            } else {
                portalContentAndDesignStore.leadsFormErrors.responseError = response.data?.error;
            }
        })
        .catch(error => {
            portalContentAndDesignStore.leadsFormErrors.validationErrors = error.response?.data?.errors;
        })
        .finally(() => {
            submittingLeadForm.value = false;
        });
}
</script>
